import { Box, Flex, Text, Icon } from "@chakra-ui/react";
import { FaInbox, FaBolt, FaUser, FaChartBar, FaBook, FaSearch } from "react-icons/fa";

// A genuinely mobile-shaped recreation of the Rosa inbox — not the desktop
// dashboard squeezed down, but how a support-inbox app actually looks on a
// phone: a single scrollable ticket list under a search bar, with a native
// bottom tab bar. Mirrors how WobbleShowcase swaps in a phone-shaped mock
// (WobbleBookingMock) at the same breakpoint instead of shrinking its
// desktop admin view.

const TABS = [
  { label: "Levelek", icon: FaInbox, active: true },
  { label: "AI", icon: FaBolt, badge: "5" },
  { label: "Enyém", icon: FaUser, badge: "9" },
  { label: "Statisztika", icon: FaChartBar },
  { label: "Tudás", icon: FaBook },
];

const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
  "Emberre vár": { bg: "#FEF3C7", color: "#92400E" },
  "Megoldva": { bg: "#D1FAE5", color: "#065F46" },
  "Ügyfélre vár": { bg: "#DBEAFE", color: "#1E40AF" },
};

const TICKETS = [
  { name: "Lindsay Lueilwitz", initials: "LL", time: "3 órája", subject: "Nem generálódnak a számlák", status: "Emberre vár", swatch: "#8B5CF6" },
  { name: "Robin Abshire", initials: "RA", time: "4 órája", subject: "Nem sikerül a regisztráció", status: "Emberre vár", swatch: "#F59E0B" },
  { name: "Edward Monahan", initials: "EM", time: "9 órája", subject: "Szolgáltatás időtartamának beállítása", status: "Megoldva", swatch: "#EC4899" },
  { name: "Jody Dibbert", initials: "JD", time: "11 órája", subject: "Online jelenlét fejlesztése", status: "Ügyfélre vár", swatch: "#10B981" },
];

const RosaMobilePreview = () => (
  <Flex direction="column" bg="white">
    {/* Top bar */}
    <Flex align="center" justify="space-between" px={4} py={3} borderBottom="1px solid" borderColor="chalk.300">
      <Flex align="center" gap={2}>
        <Flex
          w="26px"
          h="26px"
          borderRadius="full"
          bgGradient="linear(to-br, bob.300, rosa.600)"
          color="white"
          align="center"
          justify="center"
          fontSize="11px"
          fontWeight="800"
          flexShrink={0}
        >
          R
        </Flex>
        <Text fontSize="sm" fontWeight="800" color="ink.900">
          Rosa
        </Text>
      </Flex>
      <Flex align="center" gap={1} fontSize="10px" fontWeight="700" color="rosa.700" bg="rosa.50" borderRadius="full" px={2.5} py={1}>
        <Icon as={FaBolt} boxSize="9px" />
        5 AI várólistán
      </Flex>
    </Flex>

    {/* Search */}
    <Box px={4} pt={3.5} pb={2}>
      <Text fontSize="lg" fontWeight="800" color="ink.900" mb={0.5}>
        Beérkező levelek
      </Text>
      <Text fontSize="xs" color="slate.500" mb={3}>
        Minden ügyfélmegkeresés egy helyen.
      </Text>
      <Flex align="center" gap={2} bg="chalk.100" borderRadius="full" px={3} py={2}>
        <Icon as={FaSearch} boxSize="10px" color="slate.400" />
        <Text fontSize="xs" color="slate.400">
          Keresés...
        </Text>
      </Flex>
    </Box>

    {/* Ticket list */}
    <Flex direction="column">
      {TICKETS.map((t, i) => {
        const style = STATUS_STYLES[t.status];
        return (
          <Flex
            key={t.name}
            align="flex-start"
            gap={3}
            px={4}
            py={3}
            bg={i === 0 ? "rosa.50" : "transparent"}
            borderLeft="3px solid"
            borderColor={i === 0 ? "rosa.500" : "transparent"}
            borderBottom="1px solid"
            borderBottomColor="chalk.200"
          >
            <Flex
              w="30px"
              h="30px"
              borderRadius="full"
              bg={t.swatch}
              color="white"
              align="center"
              justify="center"
              fontSize="11px"
              fontWeight="700"
              flexShrink={0}
              mt={0.5}
            >
              {t.initials}
            </Flex>
            <Box flex="1" minW={0}>
              <Flex justify="space-between" align="baseline" gap={2}>
                <Text fontSize="sm" fontWeight="700" color="ink.900" noOfLines={1}>
                  {t.name}
                </Text>
                <Text fontSize="10px" color="slate.400" flexShrink={0}>
                  {t.time}
                </Text>
              </Flex>
              <Text fontSize="xs" color="slate.500" noOfLines={1} mb={1.5}>
                {t.subject}
              </Text>
              <Box display="inline-block" fontSize="10px" fontWeight="700" px={2} py={0.5} borderRadius="full" bg={style.bg} color={style.color}>
                {t.status}
              </Box>
            </Box>
          </Flex>
        );
      })}
    </Flex>

    {/* Native-style bottom tab bar */}
    <Flex borderTop="1px solid" borderColor="chalk.300" px={2} py={2.5} justify="space-around">
      {TABS.map((tab) => (
        <Flex key={tab.label} direction="column" align="center" gap={1} position="relative" px={1}>
          <Box position="relative">
            <Icon as={tab.icon} boxSize="14px" color={tab.active ? "rosa.600" : "slate.400"} />
            {tab.badge && (
              <Flex
                position="absolute"
                top="-5px"
                right="-7px"
                w="12px"
                h="12px"
                borderRadius="full"
                bg="rosa.500"
                color="white"
                align="center"
                justify="center"
                fontSize="7px"
                fontWeight="700"
              >
                {tab.badge}
              </Flex>
            )}
          </Box>
          <Text fontSize="8px" fontWeight={tab.active ? "700" : "500"} color={tab.active ? "rosa.600" : "slate.400"}>
            {tab.label}
          </Text>
        </Flex>
      ))}
    </Flex>
  </Flex>
);

export default RosaMobilePreview;
